/* ═══════════════════════════════════════════════════════════════════
   KpiBenchmarkBuilder — Step 3 component for brand reps to set concrete
   numeric KPI targets that ship inside the RFP and are measured live
   within the campaign environment.

   Each benchmark has:
     - metric key  (e.g., 'roas', 'reach', 'engagement_rate', 'ctr', 'conversions')
     - label
     - unit (× | % | absolute count)
     - target numeric value
     - comparator ('>=' for all by default)

   Persists via PUT /api/brand/waves/{wave_id}/kpi-benchmarks.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Plus, X, Check, TrendingUp, Eye, Heart, DollarSign, MousePointerClick, ShoppingCart, Footprints, Star, CalendarCheck, Store } from 'lucide-react';
import { T, P, CARD, BORDER, ATTRIBUTION_DISCLOSURE, UNMEASURED_KPI_KEYS } from './constants';
import { useAuth } from '../../contexts/AuthContext';
import { formatReach as formatCount, formatMoney } from '../../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

// Each benchmark declares `scales` — the set of brand_scale values it's
// relevant for. A single-location med-spa doesn't care about ShopMy GMV;
// a DTC brand doesn't track walk-ins. Catalog filters against user.brand_scale
// before rendering the "Add metric" bar.
export const BENCHMARK_CATALOG = [
  // Universal (shown to all scales)
  { key: 'reach',           label: 'Total Reach',          unit: '',    icon: Eye,                defaultTarget: 500000, format: (v) => formatCount(v),             hint: 'Unique impressions across platforms', scales: ['single_location', 'multi_location', 'dtc_national', 'enterprise_national'] },
  { key: 'engagement_rate', label: 'Engagement Rate',      unit: '%',   icon: Heart,              defaultTarget: 3,     format: (v) => `${Number(v).toFixed(1)}%`,  hint: '(Likes + comments + shares) ÷ impressions', scales: ['single_location', 'multi_location', 'dtc_national', 'enterprise_national'] },
  // National / DTC / Enterprise
  { key: 'roas',            label: 'Return on Ad Spend',   unit: '×',   icon: TrendingUp,         defaultTarget: 4,     format: (v) => `${Number(v).toFixed(1)}×`,   hint: 'Revenue ÷ Wave Spend',                scales: ['multi_location', 'dtc_national', 'enterprise_national'] },
  { key: 'ctr',             label: 'Click-Through Rate',   unit: '%',   icon: MousePointerClick,  defaultTarget: 2,     format: (v) => `${Number(v).toFixed(1)}%`,  hint: 'Clicks ÷ impressions',                scales: ['single_location', 'multi_location', 'dtc_national', 'enterprise_national'] },
  { key: 'conversions',     label: 'Conversions',          unit: '',    icon: ShoppingCart,       defaultTarget: 1000,  format: (v) => formatCount(v),             hint: 'Purchases / sign-ups attributed to wave', scales: ['multi_location', 'dtc_national', 'enterprise_national'] },
  { key: 'gmv',             label: 'GMV / Revenue',        unit: '$',   icon: DollarSign,         defaultTarget: 250000, format: (v) => formatMoney(v),             hint: 'Gross merchandise value (ShopMy + native)', scales: ['dtc_national', 'enterprise_national'] },
  // Local
  { key: 'walk_ins',        label: 'Walk-ins / Foot Traffic', unit: '', icon: Footprints,         defaultTarget: 500,   format: (v) => formatCount(v),             hint: 'Store visits attributed to the wave',  scales: ['single_location', 'multi_location'] },
  { key: 'rsvps',           label: 'Event RSVPs',          unit: '',    icon: CalendarCheck,      defaultTarget: 150,   format: (v) => formatCount(v),             hint: 'Link-in-bio RSVP / booking clicks',    scales: ['single_location', 'multi_location'] },
  { key: 'review_lift',     label: 'Review Lift',          unit: '',    icon: Star,               defaultTarget: 40,    format: (v) => `+${formatCount(v)}`,       hint: 'New 4-5 star Google/Yelp reviews during flight', scales: ['single_location', 'multi_location'] },
  // Enterprise / Retail
  { key: 'retail_lift',     label: 'Retail Velocity Lift', unit: '%',   icon: Store,              defaultTarget: 12,    format: (v) => `+${Number(v).toFixed(1)}%`, hint: 'Units-per-store-per-week lift vs control', scales: ['enterprise_national'] },
];

// `formatCount` is now `formatReach` from /lib/format.js (aliased on import).
// Direct M-rollup is handled there; do not reintroduce a hand-rolled version.

// Default benchmark rows per brand_scale. Fires on first open when `initial`
// from parent is empty.
const DEFAULT_KEYS_BY_SCALE = {
  single_location:     ['walk_ins', 'rsvps', 'reach', 'review_lift'],
  multi_location:      ['walk_ins', 'roas', 'reach', 'engagement_rate'],
  dtc_national:        ['roas', 'reach', 'engagement_rate'],
  enterprise_national: ['retail_lift', 'roas', 'reach', 'gmv'],
};

export default function KpiBenchmarkBuilder({ waveId, initial = [], onChange, goal, skuId, platforms }) {
  const { user } = useAuth();
  const brandScale = user?.brand_scale || 'dtc_national';
  const defaultKeys = DEFAULT_KEYS_BY_SCALE[brandScale] || DEFAULT_KEYS_BY_SCALE.dtc_national;

  const [rows, setRows] = useState(
    initial.length
      ? initial
      : defaultKeys.map(k => pickDefault(k)),
  );
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  // iter 61: auto-populate targets from historical aggregate performance.
  const [recSource, setRecSource] = useState(null);   // 'aggregate_same_sku' | 'aggregate_vertical_goal' | 'aggregate_this_brand' | 'scale_defaults'
  const [recSampleSize, setRecSampleSize] = useState(0);
  const [recsByKey, setRecsByKey] = useState({});     // { reach: {target, rationale}, ... }

  useEffect(() => { onChange?.(rows); }, [rows, onChange]);

  // Fetch platform recommendations on mount + whenever goal/sku change.
  // If initial rows came from the parent (e.g. user returned to Step 2) we
  // DON'T overwrite their edits — we just expose the recommendations as
  // inline "Apply recommended" hints.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (goal)     params.set('goal', goal);
        if (skuId)    params.set('sku_id', skuId);
        if (platforms?.length) params.set('platforms', platforms.join(','));
        const r = await axios.get(`${API}/api/brand/kpi-benchmarks/recommend?${params}`, { withCredentials: true });
        if (cancelled) return;
        setRecSource(r.data.source);
        setRecSampleSize(r.data.sample_size || 0);
        setRecsByKey(r.data.recommendations || {});
        // On first open (no initial + no user edits), auto-apply recs to
        // any matching default row so the brand lands on grounded targets.
        setRows(prev => prev.map(row => {
          const rec = r.data.recommendations?.[row.key];
          const isStillDefault = row.target === pickDefault(row.key).target;
          if (rec?.target != null && isStillDefault) {
            return { ...row, target: rec.target, auto_applied: true };
          }
          return row;
        }));
      } catch (e) {
        // Non-fatal: the builder still works with scale defaults.
        if (process.env.NODE_ENV !== 'production') console.warn('KPI recs fetch failed:', e?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [goal, skuId, platforms?.length]);

  const addMetric = (key) => {
    if (rows.some(r => r.key === key)) return;
    setRows(prev => [...prev, pickDefault(key)]);
  };

  const removeMetric = (key) => {
    setRows(prev => prev.filter(r => r.key !== key));
  };

  const updateTarget = (key, target) => {
    setRows(prev => prev.map(r => r.key === key ? { ...r, target } : r));
  };

  const save = async () => {
    if (!waveId) {
      setSaveState('saved'); // draft — will persist when wave is created
      setTimeout(() => setSaveState('idle'), 1500);
      return;
    }
    setSaveState('saving');
    try {
      await axios.put(`${API}/api/brand/waves/${waveId}/kpi-benchmarks`,
        { benchmarks: rows.map(r => ({ key: r.key, label: r.label, unit: r.unit, target: Number(r.target), comparator: '>=' })) },
        { withCredentials: true });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (err) {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 2500);
    }
  };

  // Filter catalog by this brand's scale + exclude already-added rows.
  const availableToAdd = BENCHMARK_CATALOG
    .filter(c => c.scales.includes(brandScale))
    .filter(c => !rows.some(r => r.key === c.key));

  return (
    <div className="rounded-xl p-5" style={{ background: CARD, border: `1px solid ${BORDER}` }} data-testid="kpi-benchmark-builder">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4" style={{ color: T }} />
          <h3 className="text-sm font-black text-white uppercase tracking-wide">Campaign KPI Benchmarks</h3>
          <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase"
                style={{ background: `${P}22`, color: P, border: `1px solid ${P}40` }}>
            Ships with RFP
          </span>
        </div>
        <button onClick={save}
                disabled={saveState === 'saving'}
                className="px-3 py-1.5 rounded-lg text-[10px] font-black flex items-center gap-1.5 transition-all hover:scale-[1.02] disabled:opacity-50"
                style={{ background: saveState === 'saved' ? 'rgba(74,222,128,0.2)' : `linear-gradient(135deg, ${T}, ${P})`, color: saveState === 'saved' ? '#4ade80' : '#000' }}
                data-testid="save-benchmarks-btn">
          {saveState === 'saved' ? <><Check className="h-3 w-3" /> Saved</> : saveState === 'saving' ? 'Saving…' : 'Save to RFP'}
        </button>
      </div>

      {/* iter 61: "Where did these targets come from?" reassurance strip. */}
      <RecSourceBadge source={recSource} sampleSize={recSampleSize} />

      <p className="text-[10px] text-gray-500 mb-3 leading-relaxed">
        Lock concrete numeric targets into the RFP. Each benchmark is auto-pushed to creators,
        tracked live in the campaign dashboard, and flagged on-track / at-risk / missed as engagement unfolds.
      </p>

      {/* Benchmark rows */}
      <div className="space-y-2 mb-3">
        {rows.map(r => {
          const cat = BENCHMARK_CATALOG.find(c => c.key === r.key);
          const Icon = cat?.icon || Target;
          return (
            <div key={r.key} className="rounded-lg px-3 py-2.5 flex items-center gap-3"
                 style={{ background: '#0A0A0A', border: `1px solid ${BORDER}` }}
                 data-testid={`benchmark-row-${r.key}`}>
              <Icon className="h-4 w-4 flex-shrink-0" style={{ color: T }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white">{r.label}</p>
                <p className="text-[9px] text-gray-500 truncate">{cat?.hint}</p>
                {UNMEASURED_KPI_KEYS.includes(r.key) && (
                  <p className="text-[8px] text-amber-400/90 leading-snug mt-0.5"
                     data-testid={`benchmark-disclosure-${r.key}`}>
                    {ATTRIBUTION_DISCLOSURE}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-gray-500 uppercase font-bold">Target</span>
                {r.unit === '$' && <span className="text-[11px] text-gray-400 font-bold">$</span>}
                <input type="number" min="0" step="any"
                       value={r.target}
                       onChange={(e) => updateTarget(r.key, e.target.value)}
                       className="w-24 rounded text-xs font-black text-white tabular-nums px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#00F5FF]"
                       style={{ background: '#1A1A1A', border: `1px solid ${BORDER}` }}
                       data-testid={`benchmark-target-${r.key}`} />
                {r.unit && r.unit !== '$' && <span className="text-[11px] text-gray-400 font-bold">{r.unit}</span>}

                {/* iter 61: if a platform recommendation exists AND the user's
                    current value differs, offer a one-tap "use rec" pill. */}
                {(() => {
                  const rec = recsByKey[r.key];
                  if (!rec || Number(rec.target) === Number(r.target)) return null;
                  return (
                    <button onClick={() => updateTarget(r.key, rec.target)}
                            title={rec.rationale}
                            data-testid={`apply-rec-${r.key}`}
                            className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full hover:brightness-110"
                            style={{ background: 'rgba(0,212,255,0.12)', color: T, border: `1px solid ${T}60` }}>
                      rec: {cat?.format ? cat.format(rec.target) : rec.target}
                    </button>
                  );
                })()}

                <button onClick={() => removeMetric(r.key)} className="ml-1 text-gray-600 hover:text-red-400 transition-colors"
                        data-testid={`remove-benchmark-${r.key}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="text-center py-6 text-[11px] text-gray-500">
            No benchmarks set. Add one below to lock in measurable success criteria.
          </div>
        )}
      </div>

      {/* Add more */}
      {availableToAdd.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-3 border-t" style={{ borderColor: BORDER }}>
          <span className="text-[9px] uppercase tracking-wider text-gray-500 font-bold self-center mr-1">Add metric:</span>
          {availableToAdd.map(c => (
            <button key={c.key} onClick={() => addMetric(c.key)}
                    className="px-2.5 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 transition-all hover:scale-[1.05]"
                    style={{ background: '#1A1A1A', color: T, border: `1px solid ${T}30` }}
                    data-testid={`add-benchmark-${c.key}`}>
              <Plus className="h-3 w-3" /> {c.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function pickDefault(key) {
  const cat = BENCHMARK_CATALOG.find(c => c.key === key);
  return {
    key,
    label: cat.label,
    unit: cat.unit,
    target: cat.defaultTarget,
    comparator: '>=',
  };
}

// iter 61: "Where did these targets come from?" reassurance strip. Gives
// brand reps the confidence to leave the auto-populated numbers alone.
function RecSourceBadge({ source, sampleSize }) {
  if (!source) return null;
  const copy = {
    aggregate_same_sku:       { label: 'Auto-filled from this SKU\'s history', color: '#4ade80', detail: `p75 of ${sampleSize} prior wave${sampleSize === 1 ? '' : 's'}` },
    aggregate_vertical_goal:  { label: 'Auto-filled from matching waves on platform', color: T, detail: `p75 of ${sampleSize} wave${sampleSize === 1 ? '' : 's'} with this goal in your vertical` },
    aggregate_this_brand:     { label: 'Auto-filled from your past waves', color: T, detail: `p75 of ${sampleSize} prior brand wave${sampleSize === 1 ? '' : 's'}` },
    scale_defaults:           { label: 'Starter targets — not yet based on your history', color: '#6B7280', detail: 'Will improve after your first waves complete' },
  }[source];
  if (!copy) return null;
  return (
    <div className="rounded-md px-2.5 py-1.5 mb-3 flex items-center gap-2 flex-wrap"
         style={{ background: `${copy.color}12`, border: `1px solid ${copy.color}40` }}
         data-testid="kpi-rec-source-badge">
      <span className="text-[9px] uppercase tracking-[0.2em] font-black" style={{ color: copy.color }}>
        {copy.label}
      </span>
      <span className="text-[9px] text-gray-500 font-semibold">{copy.detail}</span>
    </div>
  );
}
